import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  Flag,
  AlertCircle,
  MessageCircle,
  Copy,
  Check,
  X,
} from "lucide-react";
import { conversationsApi } from "../api/client";
import {
  fmtNumber,
  fmtDate,
  fmtDateShort,
  fmtRelative,
  themeLabel,
} from "../utils/format";

const Skeleton = ({
  width = "100%",
  height = 20,
  borderRadius = 4,
  style = {},
}) => (
  <div
    className="skeleton-box"
    style={{
      display: "inline-block",
      width,
      height,
      borderRadius,
      ...style,
    }}
  />
);

export default function Conversations() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("all");
  const [filters, setFilters] = useState({
    status: "",
    from: "",
    to: "",
    has_flags: false,
    has_unanswered: false,
    page: 1,
    page_size: 25,
  });
  const [data, setData] = useState({
    items: [],
    total: 0,
    page: 1,
    page_size: 25,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copiedId, setCopiedId] = useState(null);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);

    const params = { ...filters };
    if (!params.has_flags) delete params.has_flags;
    if (!params.has_unanswered) delete params.has_unanswered;
    if (!params.status) delete params.status;
    if (!params.from) delete params.from;
    if (!params.to) delete params.to;

    if (activeTab === "user-wise") {
      delete params.has_flags;
      delete params.has_unanswered;
      delete params.status;
    }

    const loader = activeTab === "user-wise"
      ? conversationsApi.users(params)
      : conversationsApi.list(params);

    loader
      .then((d) => !cancelled && setData(d))
      .catch(
        (e) => !cancelled && setError(e?.response?.data?.error || e.message),
      )
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, [filters, activeTab]);

  const update = (patch) => setFilters((f) => ({ ...f, ...patch, page: 1 }));
  const switchTab = (tab) => {
    setActiveTab(tab);
    setFilters((f) => ({ ...f, page: 1 }));
  };

  const downloadCsv = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
  };

  const fromRef = useRef(null);
  const toRef = useRef(null);
  const today = new Date().toISOString().slice(0, 10);
  const hasDateFilter = Boolean(filters.from || filters.to);
  const hasCompleteDateRange = Boolean(filters.from && filters.to);

  const handleFromChange = (val) => {
    let from = val || "";
    if (from && from > today) from = today;
    let to = filters.to || "";
    if (to && to < from) to = from;
    setError(null);
    setFilters((f) => ({ ...f, from, to, page: 1 }));
  };

  const handleToChange = (val) => {
    let to = val || "";
    if (to && to > today) to = today;
    let from = filters.from || "";
    if (from && to && to < from) from = to;
    setError(null);
    setFilters((f) => ({ ...f, from, to, page: 1 }));
  };

  const clearDateFilter = () => {
    setError(null);
    setFilters((f) => ({ ...f, from: "", to: "", page: 1 }));
  };

  const exportMessages = async () => {
    if (!hasCompleteDateRange) {
      setError("Please select both start and end dates before exporting.");
      return;
    }

    try {
      setExporting(true);
      setError(null);
      const params = { from: filters.from, to: filters.to };
      const blob = await conversationsApi.exportMessages(params);
      const suffix = `${filters.from || 'all'}-${filters.to || 'all'}`;
      downloadCsv(blob, `messages-export-${suffix}.csv`);
    } catch (err) {
      setError(err?.response?.data?.error || err.message);
    } finally {
      setExporting(false);
    }
  };

  const rows = activeTab === "user-wise" ? data.data || [] : data.items || [];
  const page = activeTab === "user-wise" ? data.pagination?.page ?? 1 : data.page;
  const pageSize = activeTab === "user-wise" ? data.pagination?.limit ?? data.page_size : data.page_size;
  const totalCount = activeTab === "user-wise" ? data.pagination?.total ?? 0 : data.total;
  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <div style={{ width: "100%" }}>
      <style>{`
        @keyframes skeleton-loading {
          0% { background-position: 200% 0; }
          100% { background-position: -200% 0; }
        }
        .skeleton-box {
          background: linear-gradient(90deg, var(--ink-100, #f3f4f6) 25%, var(--ink-200, #e5e7eb) 50%, var(--ink-100, #f3f4f6) 75%);
          background-size: 200% 100%;
          animation: skeleton-loading 1.5s infinite linear;
        }
        .date-picker { display: inline-block; }
        .date-picker input { cursor: pointer; transition: box-shadow .12s, border-color .12s; }
        .date-picker:hover input { box-shadow: 0 6px 18px rgba(0,0,0,0.04); border-color: var(--ink-300); }
        .btn-export { transition: background .12s, color .12s, border-color .12s; }
        .btn-export:hover { background: var(--saffron); color: white; border-color: var(--saffron); }
        .btn-clear-dates {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          background: transparent;
          border: none;
          color: var(--ink-500);
          font-size: 12px;
          font-weight: 500;
          cursor: pointer;
          padding: 4px 2px;
        }
        .btn-clear-dates:hover { color: var(--red); }
      `}</style>

      {/* Hero */}
      <div
        style={{ position: "relative", marginBottom: 32, padding: "16px 0" }}
      >
        <div
          className="devanagari-watermark"
          style={{
            position: "absolute",
            top: -30,
            right: 20,
            fontSize: 180,
            background:
              "linear-gradient(135deg, rgba(194,108,27,0.08) 0%, rgba(194,108,27,0.01) 100%)",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            pointerEvents: "none",
            userSelect: "none",
            fontWeight: 800,
          }}
        >
          संवाद
        </div>
        <div
          className="t-eyebrow"
          style={{ color: "var(--ink-500)", letterSpacing: "0.05em" }}
        >
          Sangathana Console / Beta Tester Conversations
        </div>
        <h1
          className="t-display"
          style={{
            fontSize: 36,
            marginTop: 8,
            marginBottom: 12,
            color: "var(--ink-900)",
          }}
        >
          Beta tester dialogues.
        </h1>
        <p
          style={{
            color: "var(--ink-600)",
            fontFamily: "var(--serif)",
            fontSize: 16,
            fontStyle: "italic",
            maxWidth: 720,
            lineHeight: 1.5,
          }}
        >
          {activeTab === "user-wise" ? (
            <>
              {fmtNumber(data.pagination?.total ?? 0)} user{(data.pagination?.total ?? 0) === 1 ? "" : "s"} · {fmtNumber(data.total_sessions ?? 0)} session{(data.total_sessions ?? 0) === 1 ? "" : "s"}
            </>
          ) : (
            <>
              {fmtNumber(data.total)} conversation{data.total === 1 ? "" : "s"} in scope.
            </>
          )}
        </p>
      </div>


      {/* Filters */}
      <div
        className="panel"
        style={{
          padding: "20px 24px",
          marginBottom: 24,
          background: "#ffffff",
          borderRadius: 16,
          border: "1px solid var(--ink-100)",
          boxShadow: "0 4px 20px -4px rgba(0, 0, 0, 0.02)",
        }}
      >
        <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: 16 }}>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
            <button
              className="btn"
              onClick={() => update({ has_flags: !filters.has_flags })}
              style={{
                padding: "8px 14px",
                fontSize: 13,
                background: filters.has_flags
                  ? "var(--saffron-tint)"
                  : "transparent",
                borderColor: filters.has_flags
                  ? "var(--saffron)"
                  : "var(--ink-200)",
                color: filters.has_flags
                  ? "var(--saffron-deep)"
                  : "var(--ink-700)",
                fontWeight: filters.has_flags ? 600 : 500,
                boxShadow: filters.has_flags
                  ? "inset 0 1px 3px rgba(194, 108, 27, 0.1)"
                  : "none",
              }}
            >
              <Flag size={14} /> Flagged
            </button>
            <button
              className="btn"
              onClick={() =>
                update({ has_unanswered: !filters.has_unanswered })
              }
              style={{
                padding: "8px 14px",
                fontSize: 13,
                background: filters.has_unanswered
                  ? "var(--red-tint)"
                  : "transparent",
                borderColor: filters.has_unanswered
                  ? "var(--red)"
                  : "var(--ink-200)",
                color: filters.has_unanswered ? "var(--red)" : "var(--ink-700)",
                fontWeight: filters.has_unanswered ? 600 : 500,
                boxShadow: filters.has_unanswered
                  ? "inset 0 1px 3px rgba(232, 105, 58, 0.1)"
                  : "none",
              }}
            >
              <AlertCircle size={14} /> Unanswered
            </button>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", gap: 16 }}>
            <div>
              <label
                className="t-eyebrow"
                style={{
                  display: "block",
                  marginBottom: 6,
                  color: "var(--ink-500)",
                }}
              >
                From
              </label>
              <div
                className="date-picker"
                onClick={() => {
                  try {
                    fromRef.current?.showPicker?.() || fromRef.current?.focus();
                  } catch (e) {
                    fromRef.current?.focus();
                  }
                }}
              >
                <input
                  ref={fromRef}
                  className="input"
                  type="date"
                  max={today}
                  value={filters.from}
                  title={filters.from || "Select start date"}
                  onChange={(e) => handleFromChange(e.target.value)}
                />
              </div>
            </div>
            <div>
              <label
                className="t-eyebrow"
                style={{
                  display: "block",
                  marginBottom: 6,
                  color: "var(--ink-500)",
                }}
              >
                To
              </label>
              <div
                className="date-picker"
                onClick={() => {
                  try {
                    toRef.current?.showPicker?.() || toRef.current?.focus();
                  } catch (e) {
                    toRef.current?.focus();
                  }
                }}
              >
                <input
                  ref={toRef}
                  className="input"
                  type="date"
                  max={today}
                  value={filters.to}
                  title={filters.to || "Select end date"}
                  onChange={(e) => handleToChange(e.target.value)}
                />
              </div>
            </div>
            {hasDateFilter && (
              <button
                type="button"
                className="btn-clear-dates"
                onClick={clearDateFilter}
                style={{ marginBottom: 10 }}
              >
                <X size={12} /> Clear dates
              </button>
            )}
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button
                className="btn btn-export"
                onClick={exportMessages}
                disabled={exporting || !hasCompleteDateRange}
                style={{
                  minWidth: 100,
                  display: "inline-flex",
                  justifyContent: "center",
                  alignItems: "center",
                  textAlign: "center",
                }}
                title={!hasCompleteDateRange ? "Select both dates to export" : "Export CSV"}
              >
                {exporting ? "Exporting…" : "Export CSV"}
              </button>
            </div>
          </div>
        </div>
      </div>

      {error && (
        <div
          style={{
            padding: "16px 20px",
            borderRadius: 12,
            border: "1px solid var(--red)",
            background: "#fff1f2",
            color: "var(--red)",
            marginBottom: 24,
            display: "flex",
            alignItems: "center",
            gap: 12,
            fontWeight: 500,
          }}
        >
          <AlertCircle size={20} />
          {error}
        </div>
      )}

      {/* Table */}
      <div
        className="panel"
        style={{
          background: "#ffffff",
          borderRadius: 16,
          border: "1px solid var(--ink-100)",
          boxShadow: "0 4px 20px -4px rgba(0, 0, 0, 0.02)",
          overflow: "hidden",
        }}
      >
        <table className="table">
          <thead>
            <tr>
              {activeTab === "user-wise" ? (
                <>
                  <th>User</th>
                  <th style={{ textAlign: "right" }}>Sessions</th>
                </>
              ) : (
                <>
                      <th>Session ID</th>
                      <th>Themes</th>
                      <th>Date</th>
                      <th style={{ textAlign: "right" }}>Messages</th>
                      <th style={{ textAlign: "right" }}>Unanswered</th>
                      <th style={{ textAlign: "right" }}>Flagged</th>
                </>
              )}
            </tr>
          </thead>
          <tbody>
            {loading &&
              [...Array(10)].map((_, i) => (
                <tr key={i}>
                  {activeTab === "user-wise" ? (
                    <>
                      <td>
                        <div
                          style={{ display: "flex", alignItems: "center", gap: 10 }}
                        >
                          <Skeleton width={32} height={32} borderRadius={16} />
                          <div>
                            <Skeleton
                              width={100}
                              height={14}
                              style={{ display: "block", marginBottom: 4 }}
                            />
                            <Skeleton width={70} height={11} style={{ display: "block" }} />
                          </div>
                        </div>
                      </td>
                      <td>
                        <Skeleton width={140} height={14} />
                      </td>
                      <td>
                        <Skeleton width={80} height={22} borderRadius={12} />
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <Skeleton width={40} height={16} />
                      </td>
                    </>
                  ) : (
                    <>
                      <td>
                        <Skeleton width={60} height={20} borderRadius={4} />
                      </td>
                      
                      <td>
                        <Skeleton width={60} height={24} borderRadius={12} />
                      </td>
                      <td>
                        <div
                          style={{ display: "flex", gap: 6, alignItems: "center" }}
                        >
                          <Skeleton width={60} height={22} borderRadius={12} />
                        </div>
                      </td>
                      <td>
                        <div
                          style={{ display: "flex", gap: 6, alignItems: "center" }}
                        >
                          <Skeleton width={60} height={22} borderRadius={12} />
                          <Skeleton width={50} height={22} borderRadius={12} />
                        </div>
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <Skeleton width={40} height={16} />
                      </td>
                      <td style={{ textAlign: "right" }}>
                        <Skeleton width={24} height={24} borderRadius={12} />
                      </td>
                    </>
                  )}
                </tr>
              ))}
            {!loading && !rows.length && (
              <tr>
                <td
                  colSpan={activeTab === "user-wise" ? 2 : 6}
                  style={{
                    padding: "48px 24px",
                    textAlign: "center",
                    color: "var(--ink-400)",
                    fontFamily: "var(--serif)",
                    fontStyle: "italic",
                    fontSize: 16,
                    background: "var(--paper-2)",
                  }}
                >
                  No {activeTab === "user-wise" ? "users" : "conversations"} match these filters.
                </td>
              </tr>
            )}
            {!loading &&
              rows.map((row) => (
                <tr
                  key={activeTab === "user-wise" ? row.id : row.session_id}
                  className="clickable"
                  onClick={() =>
                    activeTab === "user-wise"
                      ? navigate(`/sessions/user/${row.id}`, {
                          state: {
                            displayName: `${row.firstname} ${row.lastname}`,
                            username: row.username,
                          },
                        })
                      : navigate(`/conversations/${row._id}`)
                  }
                >
                  {activeTab === "user-wise" ? (
                    <>
                      <td>
                        <div
                          style={{ display: "flex", alignItems: "center", gap: 10 }}
                        >
                          <div
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: "50%",
                              background: "var(--saffron-tint, #fdf3e7)",
                              color: "var(--saffron-deep, #92560e)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: 11,
                              fontWeight: 700,
                              flexShrink: 0,
                            }}
                          >
                            {row.name?.slice(0, 2).toUpperCase()}
                          </div>
                          <div>
                            <div
                              style={{
                                fontWeight: 600,
                                color: "var(--ink-900)",
                                fontSize: 14,
                              }}
                            >
                              {row.name}
                            </div>
                            {row.name !== row.id && (
                              <div
                                style={{ fontSize: 11, color: "var(--ink-500)" }}
                              >
                                ID: {row.id}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td
                        style={{
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        <span className="badge badge-saffron">
                          {fmtNumber(row.total_sessions)}
                        </span>
                      </td>
                    </>
                  ) : (
                    <>
                      <td>
                        <div
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 6,
                          }}
                        >
                          <code
                            style={{
                              fontSize: 11,
                              color: "var(--ink-500)",
                              fontFamily: "var(--mono)",
                              background: "var(--ink-100)",
                              padding: "2px 6px",
                              borderRadius: 4,
                            }}
                          >
                            {row.session_id}
                          </code>
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              navigator.clipboard.writeText(row._id);
                              setCopiedId(row._id);
                              setTimeout(() => setCopiedId(null), 2000);
                            }}
                            style={{
                              background: "transparent",
                              border: "none",
                              padding: 0,
                              cursor: "pointer",
                              color:
                                copiedId === row._id
                                  ? "var(--green)"
                                  : "var(--ink-400)",
                              display: "flex",
                              alignItems: "center",
                            }}
                            title="Copy Session ID"
                          >
                            {copiedId === row._id ? (
                              <Check size={12} />
                            ) : (
                              <Copy size={12} />
                            )}
                          </button>
                        </div>
                      </td>
                      <td>
                        {row.themes?.length ? (
                          <div
                            style={{
                              display: "flex",
                              gap: 6,
                              flexWrap: "wrap",
                              alignItems: "center",
                            }}
                          >
                            {row.themes.map((t) => (
                              <span key={t} className="badge badge-neutral">
                                {themeLabel(t)}
                              </span>
                            ))}
                          </div>
                        ) : (
                          <span
                            style={{
                              color: "var(--ink-400)",
                              fontSize: 13,
                              fontStyle: "italic",
                            }}
                          >
                            —
                          </span>
                        )}
                      </td>
                      <td>
                        <div style={{ fontWeight: 600, color: "var(--ink-900)" }}>
                          {fmtDate(row.started_at)}
                        </div>
                      </td>
                      <td
                        style={{
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                          fontWeight: 500,
                          color: "var(--ink-700)",
                        }}
                      >
                        <MessageCircle
                          size={13}
                          style={{
                            verticalAlign: "middle",
                            marginRight: 6,
                            color: "var(--ink-400)",
                          }}
                        />
                        {fmtNumber(row.message_count)}
                      </td>
                      <td
                        style={{
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {row.unanswered_count > 0 ? (
                          <span className="badge badge-red">
                            {row.unanswered_count}
                          </span>
                        ) : (
                          <span style={{ color: "var(--ink-400)" }}>0</span>
                        )}
                      </td>
                      <td
                        style={{
                          textAlign: "right",
                          fontVariantNumeric: "tabular-nums",
                        }}
                      >
                        {row.flagged_count > 0 ? (
                          <span className="badge badge-saffron">
                            {row.flagged_count}
                          </span>
                        ) : (
                          <span style={{ color: "var(--ink-400)" }}>0</span>
                        )}
                      </td>
                    </>
                  )}
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginTop: 24,
          fontSize: 13,
          color: "var(--ink-600)",
          fontWeight: 500,
        }}
      >
        <span>
          Showing {rows.length ? (page - 1) * pageSize + 1 : 0}
          –{(page - 1) * pageSize + rows.length} of{" "}
          {fmtNumber(totalCount)}
        </span>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            className="btn"
            disabled={page <= 1}
            onClick={() => setFilters((f) => ({ ...f, page: page - 1 }))}
          >
            <ChevronLeft size={14} /> Prev
          </button>
          <div
            style={{
              padding: "6px 14px",
              alignSelf: "center",
              background: "var(--paper-2)",
              borderRadius: 8,
              fontFamily: "var(--mono)",
              fontSize: 13,
              color: "var(--ink-700)",
            }}
          >
            {page} / {totalPages}
          </div>
          <button
            className="btn"
            disabled={page >= totalPages}
            onClick={() => setFilters((f) => ({ ...f, page: page + 1 }))}
          >
            Next <ChevronRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}